/**
 * BookInfographicDiagramPreviewPage — COMPARISON SAMPLE.
 * A one-off preview page that renders a diagrammatic infographic using
 * the extended AI schema from POST /api/book/infographic-diagram.
 *
 * Purpose: side-by-side comparison against NotebookLM output before
 * scoping a full build. NOT wired into Bodha/Sankalpa, NOT the same
 * URL as the production /infographic/:bookId route.
 *
 * URL: /infographic-diagram/:bookId
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Lightbulb, Eye, Sprout, Zap, ClipboardCheck, Handshake,
  Trash2, TrendingUp, Sparkles,
} from "lucide-react";

import Navigation from "../components/homev3/Navigation";
import Footer from "../components/homev3/Footer";
import ParchmentBackground from "../components/ParchmentBackground";
import BalanceScale from "../components/diagrams/BalanceScale";
import { bookExamples } from "../data/bookExamples";
import { T } from "../components/homev3/tokens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/book/infographic-diagram`;

const ICON_MAP = {
  "lightbulb": Lightbulb,
  "eye": Eye,
  "root": Sprout,      // no root icon — Sprout is the closest metaphor
  "lever": Zap,        // no lever — Zap conveys leverage/impact
  "clipboard": ClipboardCheck,
  "handshake": Handshake,
  "trash": Trash2,
  "trending-up": TrendingUp,
  "sparkles": Sparkles,
};

function SectionCard({ section }) {
  const Icon = ICON_MAP[section.icon] || Sparkles;
  return (
    <article
      style={{
        background: "rgba(255,246,220,0.55)",
        border: `1px solid ${T.GOLD}55`,
        borderRadius: 8,
        padding: "16px 18px",
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        minHeight: 110,
      }}
    >
      <div style={{ flexShrink: 0, marginTop: 2 }}>
        <Icon size={30} strokeWidth={1.2} color={T.GOLD} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{
          fontFamily: T.F_HEADING, fontSize: 10, letterSpacing: "0.14em",
          color: T.GOLD, margin: 0, marginBottom: 4,
        }}>
          {section.kicker}
        </p>
        <h3 style={{
          fontFamily: T.F_HEADING, fontSize: 14, color: T.DARK_BROWN,
          margin: 0, marginBottom: 6, letterSpacing: "0.02em",
        }}>
          {section.title}
        </h3>
        <p style={{
          fontFamily: T.F_BODY, fontSize: 13, lineHeight: 1.5,
          color: "rgba(74,42,21,0.85)", margin: 0,
        }}>
          {section.body}
        </p>
      </div>
    </article>
  );
}

function OrnamentalCorner({ pos }) {
  // Simple SVG corner flourish — publisher-frame vibe.
  const rotate = { tl: 0, tr: 90, br: 180, bl: 270 }[pos] || 0;
  return (
    <div style={{
      position: "absolute", width: 36, height: 36,
      [pos.includes("t") ? "top" : "bottom"]: 8,
      [pos.includes("l") ? "left" : "right"]: 8,
      transform: `rotate(${rotate}deg)`,
      pointerEvents: "none",
    }}>
      <svg viewBox="0 0 36 36" width="36" height="36">
        <path d="M 4 4 L 20 4 M 4 4 L 4 20 M 4 4 Q 12 12 20 12 M 4 4 Q 12 12 12 20"
              stroke={T.GOLD} strokeWidth="1" fill="none" opacity="0.8" />
      </svg>
    </div>
  );
}

function ProcessTrio({ trio }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24,
      padding: "24px 20px", background: "rgba(255,246,220,0.55)",
      border: `1px solid ${T.GOLD}55`, borderRadius: 8, marginTop: 22,
    }}>
      {trio.map((step, i) => {
        const Icon = ICON_MAP[step.icon] || Sparkles;
        return (
          <div key={i} style={{ textAlign: "center" }}>
            <div style={{
              width: 62, height: 62, borderRadius: "50%",
              background: `${T.GOLD}22`, border: `1.5px solid ${T.GOLD}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 10px",
            }}>
              <Icon size={26} strokeWidth={1.2} color={T.DARK_BROWN} />
            </div>
            <p style={{
              fontFamily: T.F_HEADING, fontSize: 12, letterSpacing: "0.12em",
              color: T.DARK_BROWN, margin: 0, marginBottom: 4,
            }}>{step.label}</p>
            <p style={{
              fontFamily: T.F_BODY, fontSize: 11, fontStyle: "italic",
              color: "rgba(74,42,21,0.7)", margin: 0, lineHeight: 1.4,
            }}>{step.sublabel}</p>
          </div>
        );
      })}
    </div>
  );
}

function LoadingCard() {
  return (
    <div style={{ textAlign: "center", padding: 60, fontFamily: T.F_BODY, color: T.DARK_BROWN }}>
      <p style={{ fontFamily: T.F_HEADING, fontSize: 12, letterSpacing: "0.16em", color: T.GOLD, margin: 0 }}>
        WEAVING THE DIAGRAM
      </p>
      <p style={{ fontStyle: "italic", marginTop: 12 }}>
        The pipeline is composing this book&apos;s central metaphor…
      </p>
    </div>
  );
}

export default function BookInfographicDiagramPreviewPage() {
  const { bookId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const snapshot = React.useMemo(() => {
    const b = bookExamples.find((x) => String(x.id) === String(bookId));
    if (!b) return null;
    return {
      id: String(b.id), title: b.title, author: b.author, summary: b.summary,
      insights: b.insights || [], actionItems: b.actionItems || [],
      coreTheme: b.coreTheme || "",
    };
  }, [bookId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.post(API, {
          book_id: String(bookId), targetLanguage: "en",
          book: snapshot,
        }, { timeout: 120000 });
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setErr(e?.message || "load failed");
      }
    })();
    return () => { alive = false; };
  }, [bookId, snapshot]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="book-infographic-diagram-page">
      <ParchmentBackground />
      <Navigation />
      <main className="flex-1 w-full" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}>
          {!data && !err && <LoadingCard />}
          {err && <p style={{ textAlign: "center", color: "#8b3a12" }}>Error: {err}</p>}
          {data && (
            <div
              data-testid="diagram-infographic"
              style={{
                position: "relative",
                background: "linear-gradient(180deg, #fbf2df 0%, #f5e8c8 100%)",
                border: `1px solid ${T.GOLD}88`,
                borderRadius: 8,
                padding: "44px 40px 36px",
                boxShadow: "0 6px 28px rgba(74,42,21,0.12)",
              }}
            >
              <OrnamentalCorner pos="tl" />
              <OrnamentalCorner pos="tr" />
              <OrnamentalCorner pos="bl" />
              <OrnamentalCorner pos="br" />

              {/* Category badge */}
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <span style={{
                  display: "inline-block", padding: "6px 20px",
                  border: `1px solid ${T.GOLD}`, borderRadius: 4,
                  fontFamily: T.F_HEADING, fontSize: 11,
                  letterSpacing: "0.24em", color: T.DARK_BROWN,
                }} data-testid="diagram-category">
                  {data.category || "—"}
                </span>
              </div>

              {/* Hero title */}
              <header style={{ textAlign: "center", marginBottom: 24 }}>
                <h1 style={{
                  fontFamily: T.F_HEADING, fontSize: 30, color: T.DARK_BROWN,
                  margin: 0, marginBottom: 8, letterSpacing: "0.01em",
                }} data-testid="diagram-hero-title">
                  {data.hero?.title || data.book?.title}
                </h1>
                <p style={{
                  fontFamily: T.F_BODY, fontStyle: "italic", fontSize: 15,
                  color: "rgba(74,42,21,0.75)", margin: 0, maxWidth: 560,
                  marginInline: "auto",
                }} data-testid="diagram-hero-subtitle">
                  {data.hero?.subtitle}
                </p>
                <p style={{
                  fontFamily: T.F_BODY, fontSize: 12, color: T.DARK_BROWN,
                  marginTop: 10, marginBottom: 0,
                }}>
                  — {data.book?.author}
                </p>
              </header>

              {/* Body: 3-col grid (left cards | center diagram | right cards) */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1.15fr 1fr",
                gap: 18,
                alignItems: "start",
                marginTop: 8,
              }}>
                {/* LEFT column — sections 1-3 */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {(data.sections || []).slice(0, 3).map((s) => (
                    <SectionCard key={s.id} section={s} />
                  ))}
                </div>

                {/* CENTER — hero_diagram */}
                <div style={{
                  background: "rgba(255,246,220,0.55)",
                  border: `1px solid ${T.GOLD}55`,
                  borderRadius: 8, padding: "18px 12px",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", gap: 12,
                }} data-testid="diagram-hero-diagram">
                  <p style={{
                    fontFamily: T.F_HEADING, fontSize: 12,
                    letterSpacing: "0.14em", color: T.GOLD,
                    margin: 0, textAlign: "center",
                  }}>
                    {data.hero?.kicker}
                  </p>
                  <h2 style={{
                    fontFamily: T.F_HEADING, fontSize: 15, color: T.DARK_BROWN,
                    margin: 0, textAlign: "center", lineHeight: 1.3,
                  }}>
                    {data.hero_diagram?.caption}
                  </h2>
                  <BalanceScale
                    leftLabel={data.hero_diagram?.left_label}
                    leftValue={data.hero_diagram?.left_value}
                    rightLabel={data.hero_diagram?.right_label}
                    rightValue={data.hero_diagram?.right_value}
                    tilt="right"
                  />
                </div>

                {/* RIGHT column — sections 4-6 */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {(data.sections || []).slice(3, 6).map((s) => (
                    <SectionCard key={s.id} section={s} />
                  ))}
                </div>
              </div>

              {/* Process trio */}
              {Array.isArray(data.process_trio) && data.process_trio.length === 3 && (
                <ProcessTrio trio={data.process_trio} />
              )}

              {/* Closer tagline */}
              <div style={{
                textAlign: "center",
                marginTop: 24, paddingTop: 20,
                borderTop: `1px solid ${T.GOLD}55`,
                fontFamily: T.F_HEADING, fontSize: 14,
                letterSpacing: "0.18em", color: T.DARK_BROWN,
              }} data-testid="diagram-closer">
                {data.closer_tagline}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
